import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { fetchUnacknowledgedFinishedBulkBatch, acknowledgeBulkBatch } from "../../services/facebookPostsService";

const POLL_INTERVAL_MS = 60000;
const POSTS_PLANNER_PATH = "/fb/posts-planner";

/**
 * Mounted once at the app shell (App.jsx) so it can surface a Facebook
 * bulk-schedule completion notice no matter which page the user is on, or
 * even after the browser was closed and reopened — there is no other
 * global toast/notification system in this codebase to hook into, so this
 * is intentionally small and scoped to exactly this one job. The Posts
 * Planner page's own progress bar already covers the same event while the
 * user is looking at it, so this stays silent on that page to avoid
 * showing the same completion twice.
 */
export default function FbBulkScheduleNotifier() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [batch, setBatch] = useState(null);

  useEffect(() => {
    if (!user) {
      setBatch(null);
      return;
    }

    let cancelled = false;
    const poll = async () => {
      const found = await fetchUnacknowledgedFinishedBulkBatch();
      if (!cancelled) setBatch(found);
    };

    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [user?.id]);

  if (!batch || location.pathname === POSTS_PLANNER_PATH) return null;

  const isSuccess = batch.status === "completed";
  const successCount = batch.total_count - batch.failed_count;

  const handleDismiss = async (e) => {
    e.stopPropagation();
    setBatch(null);
    await acknowledgeBulkBatch(batch.id);
  };

  const handleView = () => {
    navigate(POSTS_PLANNER_PATH);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-3rem)]">
      <div
        onClick={handleView}
        role="button"
        tabIndex={0}
        className="bg-white border border-gray-200 rounded-xl shadow-lg p-4 cursor-pointer hover:shadow-xl transition-shadow"
      >
        <div className="flex items-start gap-3">
          {isSuccess ? (
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800">Facebook scheduling completed</p>
            <p className="text-sm text-gray-600 mt-0.5">
              {isSuccess
                ? `${batch.total_count}/${batch.total_count} processed`
                : `${successCount} successful, ${batch.failed_count} failed`}
            </p>
            <span className="inline-block mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-800">
              View in Posts Planner →
            </span>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
